import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Coins, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useWallet } from "../hooks/useWallet";
import { useCheckout } from "../contexts/CheckoutContext";
import GlassCard from "../components/GlassCard";
import GhostButton from "../components/GhostButton";
import GoldButton from "../components/GoldButton";
import RecentActivity from "../components/RecentActivity";
import StatusPill from "../components/StatusPill";
import { listAudits } from "../lib/audits";
import { formatPrice, listPaymentsHistory } from "../lib/billing";

const FREE_DAILY_LIMIT = 3;

function shortDate(iso) {
  try {
    return new Date(iso).toLocaleDateString([], {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { plan, isPro, balance, remaining, lastUpdated } = useWallet();
  const { openCheckout } = useCheckout();

  const [recentAudits, setRecentAudits] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);

  useEffect(() => {
    if (!user) return;
    let live = true;
    (async () => {
      const [a, p] = await Promise.all([
        listAudits({ page: 1, pageSize: 3 }),
        listPaymentsHistory(1, 3),
      ]);
      if (!live) return;
      if (a.ok) setRecentAudits(a.data.items || []);
      if (p.ok) setRecentPayments(p.data.items || []);
    })();
    return () => {
      live = false;
    };
  }, [user?.id, lastUpdated]);

  if (!user) return null;

  const onUpgrade = () => openCheckout({ kind: "subscription", planId: "pro" });
  const onRecharge = () => openCheckout({ kind: "recharge", packageId: "nxt_500" });

  return (
    <section className="space-y-8" data-testid="dashboard-root">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1
            className="font-display text-3xl sm:text-4xl text-white tracking-tight"
            data-testid="dashboard-welcome"
          >
            {t("dashboard.welcome", { name: user.name })}
          </h1>
          <p className="mt-1 text-sm text-white/55">{user.email}</p>
        </div>
        <GhostButton onClick={logout} data-testid="dashboard-logout-btn">
          {t("dashboard.logout")}
        </GhostButton>
      </div>

      {/* 3-column feature row */}
      <div className="grid gap-5 lg:grid-cols-3" data-testid="dashboard-feature-row">
        {/* Plan */}
        <GlassCard className="p-6 relative overflow-hidden" data-testid="dashboard-plan-card">
          {isPro && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-35"
              style={{
                background:
                  "radial-gradient(360px 200px at 90% -10%, rgba(212,175,55,0.25), transparent)",
              }}
            />
          )}
          <div className="relative">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.18em] text-white/50">
                {t("dashboard.planLabel")}
              </span>
              <span
                data-testid="dashboard-plan-badge"
                className={
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold " +
                  (isPro
                    ? "bg-gradient-to-r from-[#F4D77A] to-[#A8862B] text-[#0a0a0d]"
                    : "border border-white/15 bg-white/[0.04] text-white/85")
                }
              >
                {isPro ? <Sparkles className="h-3 w-3" /> : null}
                {isPro ? t("plan.pro") : t("plan.free")}
              </span>
            </div>
            <div className="mt-3">
              <div className="text-sm text-white/55">
                {t("dashboard.dailyRemaining")}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className="text-3xl font-display text-white tabular-nums"
                  data-testid="dashboard-quick-remaining"
                >
                  {isPro ? "∞" : remaining}
                </span>
                <span className="text-xs text-white/40">
                  {isPro ? t("dashboard.unlimited") : `/ ${FREE_DAILY_LIMIT}`}
                </span>
              </div>
            </div>
            <div className="mt-5">
              {isPro ? (
                <Link to="/billing" data-testid="dashboard-plan-manage">
                  <GhostButton className="w-full">
                    {t("dashboard.planCtaPro")}
                  </GhostButton>
                </Link>
              ) : (
                <GoldButton
                  className="w-full justify-center"
                  onClick={onUpgrade}
                  data-testid="dashboard-plan-upgrade"
                >
                  {t("dashboard.planCtaFree")}
                </GoldButton>
              )}
            </div>
          </div>
        </GlassCard>

        {/* Wallet */}
        <GlassCard className="p-6" data-testid="dashboard-wallet-card">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.18em] text-white/50">
              {t("dashboard.nxtBalance")}
            </span>
            <Coins className="h-4 w-4 text-[#D4AF37]" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span
              className="text-4xl font-display text-white tabular-nums"
              data-testid="dashboard-nxt-balance"
            >
              {balance}
            </span>
            <span className="text-sm text-[#D4AF37]">NXT</span>
          </div>
          <div className="mt-5">
            <GoldButton
              className="w-full justify-center"
              onClick={onRecharge}
              data-testid="dashboard-wallet-recharge"
            >
              {t("dashboard.walletCta")}
            </GoldButton>
          </div>
        </GlassCard>

        {/* Auditor */}
        <GlassCard className="p-6 relative overflow-hidden" data-testid="dashboard-auditor-card">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-35"
            style={{
              background:
                "radial-gradient(360px 200px at -10% 120%, rgba(30,64,175,0.25), transparent)",
            }}
          />
          <div className="relative">
            <span className="text-xs uppercase tracking-[0.18em] text-white/50">
              {t("dashboard.auditorTitle")}
            </span>
            <p className="mt-2 text-sm text-white/65 leading-relaxed">
              {t("dashboard.auditorBody")}
            </p>
            <div className="mt-5">
              <Link to="/auditor" data-testid="dashboard-auditor-cta">
                <GoldButton className="w-full justify-center gap-2">
                  {t("dashboard.auditorCta")}
                  <ArrowRight className="h-4 w-4" />
                </GoldButton>
              </Link>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Recent activity */}
      <div className="grid gap-5 lg:grid-cols-2">
        <RecentActivity
          items={recentAudits}
          testId="dashboard-recent-audits"
          to="/history"
          emptyKey={{ titleKey: "dashboard.recentAudits" }}
          renderItem={(a) => (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-white/50 text-xs w-[70px] shrink-0">
                {shortDate(a.created_at)}
              </span>
              <span className="flex-1 truncate text-white/80">
                {(a.prompt || "").slice(0, 60)}
                {(a.prompt || "").length > 60 ? "…" : ""}
              </span>
              {a.bounced ? (
                <span className="text-xs text-amber-300">{t("history.bounce")}</span>
              ) : (
                <span
                  className="font-display tabular-nums text-sm"
                  style={{
                    color:
                      a.nexa_score >= 9
                        ? "#D4AF37"
                        : a.nexa_score >= 7
                          ? "#1E40AF"
                          : "#7F1D1D",
                  }}
                >
                  {typeof a.nexa_score === "number" ? a.nexa_score.toFixed(1) : "—"}
                </span>
              )}
            </div>
          )}
        />
        <RecentActivity
          items={recentPayments}
          testId="dashboard-recent-payments"
          to="/billing"
          emptyKey={{ titleKey: "dashboard.recentPayments" }}
          renderItem={(p) => (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-white/50 text-xs w-[70px] shrink-0">
                {shortDate(p.created_at)}
              </span>
              <span className="flex-1 text-white/80">
                {t(`billing.kind.${p.kind}`)}
                {p.nxt_amount ? (
                  <span className="ml-2 text-[#D4AF37]">+{p.nxt_amount} NXT</span>
                ) : null}
              </span>
              <span className="text-white/60 tabular-nums">
                {p.amount_cents > 0 ? formatPrice(p.amount_cents, p.currency) : "—"}
              </span>
              <StatusPill
                status={p.status}
                label={t(`billing.status.${p.status}`)}
              />
            </div>
          )}
        />
      </div>
    </section>
  );
}
